/* eslint-disable @typescript-eslint/naming-convention, camelcase */
// The js-routes helpers below take Rails' own snake_case keys (`proposal_id`, `id`),
// so the rule is off for those call sites. The `proposal` prop itself is camelCase,
// matching what proposals/datatables/_columns.json.jbuilder emits.
import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, TextInput, Checkbox, Loader, showErrorToast } from '@ui';
import I18n, { translate } from '@utils/i18n';
import api from '@utils/axios';
import { reloadWithToast } from '@utils/pendingToast';
import { viewAndAcceptProposalsPath, sendProposalEmailProposalPath, emailModalProposalsPath } from '@routes';
import './SendProposalStyles.scss';
const namespace = 'features.proposals.index.send_proposal';
const FORM_ID = 'proposal-send-form';
export default function SendProposalModal({ proposal, onClose }) {
    var _a;
    const [loading, setLoading] = useState(true);
    // Rails sends JSON `null` for a blank column and a default parameter only catches
    // `undefined`, so these are coerced rather than defaulted. A null reaching `.trim()`
    // tears down the whole island and the dialog silently never opens (IP-15897).
    const [sendToEmail, setSendToEmail] = useState((_a = proposal.contactsEmails) !== null && _a !== void 0 ? _a : '');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [bccMe, setBccMe] = useState(false);
    const [bccMultipleParticipants, setBccMultipleParticipants] = useState(false);
    const [previewLoaded, setPreviewLoaded] = useState(false);
    const isValid = sendToEmail.trim().length > 0 && emailSubject.trim().length > 0;
    const pdfPath = proposal.emailUrlUuid
        ? viewAndAcceptProposalsPath({ token: proposal.emailUrlUuid, format: 'pdf' })
        : null;
    useEffect(() => {
        api
            .get(emailModalProposalsPath({ proposal_id: proposal.id, format: 'json' }))
            .then(({ data }) => {
            var _a, _b;
            setEmailBody((_a = data.body) !== null && _a !== void 0 ? _a : '');
            setEmailSubject((_b = data.subject) !== null && _b !== void 0 ? _b : '');
        })
            .catch(() => {
            showErrorToast(String(I18n.t('generic.errors.request_error')));
        })
            .finally(() => setLoading(false));
    }, [proposal.id]);
    const onSubmit = () => {
        if (!isValid || loading)
            return;
        setLoading(true);
        const payload = new FormData();
        payload.append('send_to_email', sendToEmail);
        payload.append('email_subject', emailSubject);
        payload.append('email_body', emailBody);
        if (bccMe)
            payload.append('bcc_me', String(bccMe));
        if (bccMultipleParticipants)
            payload.append('bcc_multiple_participants', String(bccMultipleParticipants));
        api
            .post(
        // js-routes: pass the id in an options object, not positionally. A bare
        // positional arg binds to the optional `(:locale)` scope, and the helper then
        // throws `ParametersMissing: Route missing required keys: id` — synchronously,
        // inside onSubmit, so the dialog just sticks with its send button disabled and
        // nothing surfaces. Same trap documented in AlarmDeviceLegend/services.
        sendProposalEmailProposalPath({
            id: proposal.id,
            format: 'json',
            _options: true
        }), payload)
            .then(({ data }) => {
            // Reload rather than just closing. `#send_proposal_email` does more than send:
            // in the same request it writes a proposal_status_updates row and moves the
            // proposal to out_for_quote. The old Bootstrap dialog submitted a form, so the
            // page reloaded and picked all of that up; a JSON POST does not, which left the
            // status and the whole Proposal History table showing pre-send values until the
            // user reloaded by hand (IP-15962 is the same defect on invoices).
            //
            // A reload is safe here, unlike on invoices: the status change is committed
            // during this request, not by the mailer worker, so there is nothing to race.
            // It also keeps every server-rendered surface correct rather than only the
            // pieces we thought to patch.
            reloadWithToast(data.message);
        })
            .catch((error) => {
            var _a, _b, _c;
            showErrorToast((_c = (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : String(I18n.t('generic.errors.request_error')));
        })
            .finally(() => setLoading(false));
    };
    // Deliberately the browser's own PDF viewer rather than the `@ui` PDFViewer: `<object>`
    // brings page navigation, fit-to-width, rotate, download and print for free, which the
    // Quimby viewer does not have yet. Same call as IP-15897 — revisit when it grows a toolbar.
    const pdfPreview = pdfPath ? (React.createElement("div", { className: "sendProposalModal__preview" },
        !previewLoaded && (React.createElement("div", { className: "sendProposalModal__previewLoading" },
            React.createElement(Loader, null))),
        React.createElement("object", { data: pdfPath, type: "application/pdf", className: "sendProposalModal__pdf", onLoad: () => setPreviewLoaded(true) },
            React.createElement("p", null,
                React.createElement("a", { href: pdfPath, target: "_blank", rel: "noopener noreferrer" }, translate('pdf_viewer_not_supported', { namespace })))))) : null;
    return (React.createElement(Modal, { isOpen: true, onClose: onClose, variant: "base", className: "sendProposalModal" },
        React.createElement(ModalHeader, { title: translate('title', { namespace }), onClose: onClose }),
        React.createElement(ModalBody, { className: "sendProposalModal__body" },
            React.createElement("form", { id: FORM_ID, className: "sendProposalModal__form", onSubmit: (e) => {
                    e.preventDefault();
                    onSubmit();
                } },
                React.createElement(TextInput, { id: "proposal-send-email", type: "email", multiple: true, label: translate('send_to_email', { namespace }), required: true, value: sendToEmail, disabled: loading, onChange: (e) => setSendToEmail(e.target.value) }),
                React.createElement(Checkbox, { id: "proposal-send-bcc-multiple", label: translate('bcc_multiple_participants', { namespace }), checked: bccMultipleParticipants, disabled: loading, onChange: setBccMultipleParticipants }),
                React.createElement(TextInput, { id: "proposal-send-subject", label: translate('email_subject', { namespace }), required: true, value: emailSubject, disabled: loading, onChange: (e) => setEmailSubject(e.target.value) }),
                React.createElement(Checkbox, { id: "proposal-send-bcc-me", label: translate('bcc_me', { namespace }), checked: bccMe, disabled: loading, onChange: setBccMe }),
                React.createElement(TextInput, { id: "proposal-send-body", multiline: true, rows: 12, label: translate('email_body', { namespace }), value: emailBody, disabled: loading, onChange: (e) => setEmailBody(e.target.value) })),
            pdfPreview),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { onClick: onClose, disabled: loading }, I18n.t('generic.cancel')),
            React.createElement(Button, { variant: "primary", type: "submit", form: FORM_ID, disabled: loading || !isValid }, translate('send_and_close_button', { namespace })))));
}
